import React, {useContext, useMemo, useReducer, useRef, useState} from 'react'
import {makeStyles, Table, TableBody, TableCell, TableHead, TableRow} from '@material-ui/core';
import Tr from 'translations/Translations';
import BoldTypography from "components/BoldTypography";
import PropTypes from 'prop-types';
import OfferFilesTableRow from "./OfferFilesTableRow";
import PurchaserOfferContext from "context/PurchaserOfferContext";
import Form from "components/Form";
import VS from "constants/ValidationShema";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import DemandOffersOfferService from "services/DemandOffersOfferService";
import {useParams} from "react-router";
import DemandAdditionalOffersOfferService from "services/DemandAdditionalOffersOfferService";
import {OfferMode} from "../../../../common/constants/OfferMode";
import {RoundType} from "constants/RoundType";
import WorkableIconDownloadButton from "components/button/WorkableIconDownloadButton";
import iconWorksReducer from "libs/iconWorksReducer";
import OfferSectionContext from "context/OfferSectionContext";
import useSignature from "hooks/useSignature";
import useLoadingScreen from "hooks/useLoadingScreen";
import {simpleAlert} from "libs/AlertNotification";
import {SignatureType} from "constants/SignatureType";

const useStyle = makeStyles(theme => ({
    lpColumn: theme.components.table.lpColumn,
    buttonColumn: theme.components.table.buttonColumn,
    dateColumn: theme.components.table.dateColumn,
    dateTimeColumn: theme.components.table.dateTimeColumn,
    typeColumn: {
        width: 180,
        '& .MuiFormHelperText-root': {
            display: 'none'
        }
    }
}));

export const CONTRACTOR_OFFER_EDIT_FILES_FORM_ID = "cont_offer_edit_files";

function OfferFilesTable(
    {
        disabled,
        mode,
        section,
        offerEdit,
        roundType,

    }) {

    const classes = useStyle();
    const purchaserOfferContext = useContext(PurchaserOfferContext);
    const [bulkEdit, setBulkEdit] = useState(false);
    const [bulkEditWorking, setBulkEditWorking] = useState(false);
    const params = useParams();
    const realOnePositionMode = section.onePositionMode || (offerEdit.positions.length === 1 && !(roundType === RoundType.ADDITIONAL_OFFERS || mode === OfferMode.PLACED))
    const contextSection = useContext(OfferSectionContext);
    const signFileRef = useRef(null);
    const {doSignDocContent, breakSigning} = useSignature();
    const {hideLoadingScreen, showLoadingScreen} = useLoadingScreen();

    const positionIdToOrderMap = useMemo(() => offerEdit.positions.reduce((prev, cur) => {
        prev[`positionId_${cur.positionId}`] = cur.order;
        return prev;
    }, {}), [offerEdit.positions]);

    const signFileAvailable = useMemo(() => !purchaserOfferContext && offerEdit.offerFiles.some(x => x.signatureConfiguration !== null), [offerEdit.offerFiles, purchaserOfferContext]);

    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        download: false,
    });

    const onDownloadZip = () => {
        dispatchIconWorks({type: 'download', works: true});
        let promise;
        switch (roundType) {
            case RoundType.BASE:
                promise = DemandOffersOfferService.getDownloadOfferFilesZip(params.id, params.stageId, purchaserOfferContext?.supplierId)
                break;
            case RoundType.ADDITIONAL_OFFERS:
                promise = DemandAdditionalOffersOfferService.getDownloadOfferFilesZip(params.id, params.stageId, purchaserOfferContext?.supplierId)
                break;
            // case RoundType.AUCTION:
            //     promise = DemandOffersAuctionService.getDownloadOfferFile(params.id, params.stageId, mode, row.identity, purchaserOfferContext?.supplierId)
            //     break;
            default:
                throw new Error("Unknown roundType: " + roundType)
        }
        promise.finally(() => {
            dispatchIconWorks({type: 'download', works: false});
        })
    }

    const onBulkSubmit = ({form}, actions) => {
        setBulkEditWorking(true);
        const filtered = form.filter((x, i) => offerEdit.offerFiles[i].documentType !== x.documentType || offerEdit.offerFiles[i].containsRodo !== x.containsRodo).map(x => ({
            fileId: x.identity,
            documentType: x.documentType,
            containsRodo: x.containsRodo
        }))

        if (filtered.length > 0) {
            let promise;
            switch (roundType) {
                case RoundType.BASE:
                    promise = DemandOffersOfferService.editOfferFiles(params.id, params.stageId, mode, purchaserOfferContext?.supplierId, {files: filtered})
                    break;
                case RoundType.ADDITIONAL_OFFERS:
                    promise = DemandAdditionalOffersOfferService.editOfferFiles(params.id, params.stageId, mode, purchaserOfferContext?.supplierId, {files: filtered})
                    break;
                default:
                    throw new Error("Unsupported roundType: " + roundType)
            }

            promise.finally(() => {
                setBulkEdit(false);
                setBulkEditWorking(false);
            })
        } else {
            setBulkEdit(false);
            setBulkEditWorking(false);
        }
    }

    const doBreakSigning = () => {
        signFileRef.current = null;
        breakSigning();
    }

    const onSignDocument = (row) => {
        if (signFileRef.current != null) {
            return;
        }
        signFileRef.current = row;

        doSignDocContent(
            section.signatureTypes.filter(x => [SignatureType.QUALIFIED, SignatureType.PERSONAL_ID].includes(x)),
            async (sigType) => {
                let fileUrl;
                if (roundType === RoundType.BASE) {
                    fileUrl = await DemandOffersOfferService.getDownloadOfferFileUrl(params.id, params.stageId, mode, row.identity, purchaserOfferContext?.supplierId);
                } else if (roundType === RoundType.ADDITIONAL_OFFERS) {
                    fileUrl = await DemandAdditionalOffersOfferService.getDownloadOfferFileUrl(params.id, params.stageId, mode, row.identity, purchaserOfferContext?.supplierId);
                } else {
                    console.error("Sign document unavilable for roundType: +" + roundType)
                }
                if (!fileUrl) {
                    breakSigning();
                    return;
                }
                const result = await DemandOffersOfferService.getDocumentBase64EncodedToSignDocument(fileUrl);
                if (!result)
                    doBreakSigning();
                return result;

            },
            (signatureType, signature) => {
                doBreakSigning();
                showLoadingScreen();
                const data = {signatureType, signature}
                if (roundType === RoundType.BASE) {
                    DemandOffersOfferService.signFile(params.id, params.stageId, row.identity, data)
                        .then((resp) => {if(resp === true) simpleAlert("sign_document_completed")}).finally(hideLoadingScreen);
                } else if (roundType === RoundType.ADDITIONAL_OFFERS) {
                    DemandAdditionalOffersOfferService.signFile(params.id, params.stageId, row.identity, data)
                        .then((resp) => {if(resp === true) simpleAlert("sign_document_completed")}).finally(hideLoadingScreen);
                }
            },
            (error) => {
                console.error(error)
                simpleAlert("signature_error");
                doBreakSigning();
            },
            () => {
                doBreakSigning();
            },
            async () => contextSection.encryptionEnabled ? contextSection.roundId : null,
            row.signatureConfiguration
        )
    }

    const getButtons = (formikProps) => {
        const buttons = [];
        if (bulkEdit) {
            buttons.push(createButton(Tr('discard_changes'), () => {
                formikProps.resetForm();
                setBulkEdit(false);
            }, "outlined", bulkEditWorking, undefined, false, undefined));

            buttons.push(createButton(Tr('operator.modify.update_files'), () => formikProps.submitForm(),
                undefined, bulkEditWorking, undefined, bulkEditWorking, SaveIcon));

        } else {
            buttons.push(createButton(Tr('change_files_type'), () => setBulkEdit(true),
                undefined, disabled || bulkEdit, undefined, false, EditIcon));
        }
        return buttons;

    }

    return (<div>
        {!offerEdit.offerFiles.length ?
            <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
            :
            <>

                <Form
                    id={CONTRACTOR_OFFER_EDIT_FILES_FORM_ID}
                    initialValues={{
                        form: offerEdit.offerFiles
                    }}
                    validationSchema={VS.any()}
                    onSubmit={onBulkSubmit}
                >{(formikProps) => (<>
                    {
                        (purchaserOfferContext && mode === OfferMode.PLACED) &&
                        <MultiButton buttons={getButtons(formikProps)}/>
                    }
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{Tr("name")}</TableCell>
                                <TableCell className={classes.dateColumn}>{Tr("add_data")}</TableCell>
                                <TableCell className={classes.typeColumn}>{Tr("contains_rodo")}</TableCell>
                                <TableCell className={classes.typeColumn}>{Tr("type")}</TableCell>
                                <TableCell className={classes.buttonColumn}>{Tr("size")}</TableCell>
                                {!realOnePositionMode && <TableCell
                                    className={offerEdit.passiveOfferForm ? classes.dateTimeColumn : classes.dateColumn}>{Tr("assignment")}</TableCell>}
                                {(!purchaserOfferContext && (!!contextSection.digitalSign || offerEdit.passiveOfferForm)) &&
                                    <TableCell colSpan={signFileAvailable ? 2 : 1} className={classes.buttonColumn}>{Tr("file_sign_status")}</TableCell>}
                                <TableCell className={classes.buttonColumn}>
                                    {mode === OfferMode.PLACED && roundType !== RoundType.AUCTION && purchaserOfferContext?.supplierId
                                        && offerEdit.offerFiles.some(doc => !doc.inaccessible) && <WorkableIconDownloadButton
                                            onClick={onDownloadZip}
                                            disabled={iconWorks.anyWorks}
                                            working={iconWorks.download}
                                        />}
                                </TableCell>
                                {!disabled && (!purchaserOfferContext || mode === OfferMode.EDIT) && <TableCell className={classes.buttonColumn}></TableCell>}
                                {!disabled && (!purchaserOfferContext || mode === OfferMode.EDIT) && <TableCell className={classes.buttonColumn}></TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                offerEdit.offerFiles.map((doc, index) => (
                                    <OfferFilesTableRow
                                        formikProps={formikProps}
                                        index={index}
                                        classes={classes}
                                        key={doc.identity}
                                        row={doc}
                                        disabled={disabled}
                                        onePositionMode={realOnePositionMode}
                                        positionIdToOrderMap={positionIdToOrderMap}
                                        mode={mode}
                                        roundType={roundType}
                                        bulkEdit={bulkEdit}
                                        passiveOfferForm={offerEdit.passiveOfferForm}
                                        signFileAvailable={signFileAvailable}
                                        onSignDocument={onSignDocument}
                                    />))
                            }
                        </TableBody>
                    </Table>
                </>)
                }
                </Form>
            </>
        }
    </div>)
}

OfferFilesTable.propTypes = {
    offerEdit: PropTypes.shape({
        offerFiles: PropTypes.array.isRequired,
        positions: PropTypes.array.isRequired
    }).isRequired,
    roundType: PropTypes.oneOf(Object.keys(RoundType)).isRequired
};
export default OfferFilesTable;